import { useEffect, useRef, useState } from 'react';
import { formatNumberWithCommas, goodFormatNumber } from '../utils/format-numbers.ts';
import { layoutTreemap } from '../utils/treemap-layout.ts';

function hexToRgb(hex: string): [number, number, number] {
  const bigint = parseInt(hex.replace('#', ''), 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function getColor(
  value: number,
  min: number,
  max: number,
  negative: string,
  positive: string,
  neutral: string,
) {
  const [r, g, b] = hexToRgb(value === 0 ? neutral : value > 0 ? positive : negative);
  const opacity =
    value === 0
      ? 1
      : Math.min(
          1,
          Math.max(
            0.2,
            Math.pow(
              value > 0 ? value / (max > 0 ? max : 1) : Math.abs(value) / Math.abs(min),
              value > 0 ? 0.25 : 0.5,
            ),
          ),
        );
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

export interface TreemapItem {
  id?: number;
  name: string;
  value: number;
  originalValue: number;
  currency?: string;
  pnl_percent?: number;
  pnl?: number;
  quantity?: number;
  onClick?: (id: number, name: string) => void;
}

export interface ChartTreemapProps {
  items?: TreemapItem[];
  height?: string;
  colorNegative?: string;
  colorPositive?: string;
  colorNeutral?: string;
}

function formatValue(value: number, currency?: string) {
  return [goodFormatNumber(value), currency?.toUpperCase()].filter(Boolean).join(' ');
}

function formatPercent(value: number) {
  return `${value > 0 ? '+' : ''}${goodFormatNumber(value)}%`;
}

export const ChartTreemap = ({
  items = [],
  height = '370px',
  colorNegative = '#DC2626',
  colorPositive = '#16A34A',
  colorNeutral = '#9d9d9d',
}: ChartTreemapProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const updateSize = () => {
      const { width, height } = container.getBoundingClientRect();
      setSize(current =>
        current.width === width && current.height === height ? current : { width, height },
      );
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const rectangles = layoutTreemap(
    items.map(item => item.value),
    size.width,
    size.height,
    24,
  );
  const percentages = rectangles.map(({ index }) => items[index].pnl_percent ?? 0);
  const min = Math.min(0, ...percentages);
  const max = Math.max(0, ...percentages);
  const activeRectangle = rectangles.find(rectangle => rectangle.index === activeIndex);
  const activeItem = activeRectangle ? items[activeRectangle.index] : undefined;
  const tooltipWidth = Math.min(280, Math.max(0, size.width - 16));

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-white font-[Inter]"
      style={{ height }}
      role="group"
      aria-label="Assets treemap"
      onMouseLeave={() => setActiveIndex(null)}
    >
      {rectangles.map(rectangle => {
        const item = items[rectangle.index];
        const metric =
          item.pnl_percent != null
            ? formatPercent(item.pnl_percent)
            : formatValue(item.originalValue, item.currency);
        const showName = rectangle.width >= 110 && rectangle.height >= 76;
        const fontSize = Math.min(
          26,
          Math.max(12, Math.min(rectangle.width / 10, rectangle.height / 7)),
        );
        const showMetric =
          rectangle.width >= 48 &&
          rectangle.height >= 28 &&
          rectangle.width - 12 >= metric.length * 7;
        const clickable = item.id != null && item.onClick != null;
        return (
          <button
            key={rectangle.index}
            type="button"
            className="absolute flex flex-col items-center justify-center overflow-hidden border border-white p-0 text-center text-white focus-visible:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500"
            style={{
              left: rectangle.x,
              top: rectangle.y,
              width: rectangle.width,
              height: rectangle.height,
              backgroundColor: getColor(
                item.pnl_percent ?? 0,
                min,
                max,
                colorNegative,
                colorPositive,
                colorNeutral,
              ),
              cursor: clickable ? 'pointer' : 'default',
              boxShadow:
                activeIndex === rectangle.index
                  ? 'inset 0 0 0 2px rgba(255,255,255,.7)'
                  : undefined,
            }}
            aria-label={`${item.name}, ${formatValue(item.originalValue, item.currency)}${
              item.pnl_percent != null ? `, ${formatPercent(item.pnl_percent)}` : ''
            }`}
            onMouseEnter={() => setActiveIndex(rectangle.index)}
            onFocus={() => setActiveIndex(rectangle.index)}
            onBlur={() => setActiveIndex(null)}
            onClick={() => {
              if (item.id != null) item.onClick?.(item.id, item.name);
            }}
          >
            {showName && (
              <span
                className="block w-full overflow-hidden px-2 font-semibold"
                style={{
                  fontSize,
                  lineHeight: 1.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {item.name}
              </span>
            )}
            {showName && (
              <span
                className="mt-1 block max-w-full truncate px-2"
                style={{ fontSize: Math.max(12, fontSize * 0.65) }}
              >
                {formatValue(item.originalValue, item.currency)}
              </span>
            )}
            {showMetric && (item.pnl_percent != null || !showName) && (
              <span
                className="block max-w-full truncate px-1"
                style={{
                  fontSize: showName ? Math.max(12, fontSize * 0.65) : 12,
                  lineHeight: '18px',
                }}
              >
                {metric}
              </span>
            )}
          </button>
        );
      })}
      {activeItem && activeRectangle && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-20 flex flex-col gap-2 rounded-lg bg-white p-3 text-sm text-gray-800 shadow-lg"
          style={{
            width: tooltipWidth,
            left: Math.max(
              8,
              Math.min(
                size.width - tooltipWidth - 8,
                activeRectangle.x + activeRectangle.width / 2 - tooltipWidth / 2,
              ),
            ),
            ...(activeRectangle.y + activeRectangle.height / 2 > size.height / 2
              ? { bottom: size.height - activeRectangle.y - activeRectangle.height / 2 + 8 }
              : { top: activeRectangle.y + activeRectangle.height / 2 + 8 }),
          }}
        >
          <strong className="break-words">{activeItem.name}</strong>
          {activeItem.quantity != null && (
            <div>
              <strong>Quantity:</strong> {formatNumberWithCommas(activeItem.quantity)}
            </div>
          )}
          {activeItem.pnl != null && (
            <div>
              <strong>PNL:</strong> {formatValue(activeItem.pnl, activeItem.currency)}
            </div>
          )}
          {activeItem.pnl_percent != null && (
            <div>
              <strong>PNL Percent:</strong> {formatPercent(activeItem.pnl_percent)}
            </div>
          )}
          <div>
            <strong>Total:</strong> {formatValue(activeItem.originalValue, activeItem.currency)}
          </div>
        </div>
      )}
    </div>
  );
};
